import classNames from 'classnames';
import { useRef, useState } from 'react';
import { BlokkieIcon, blokkieTheme, BlokkieTypography } from '@tikkie/blokkie';
import { useClickOutside } from '@/hooks/click-outside';

import './dropdown-input.css';
import { useTranslation } from 'react-i18next';

export type Language = 'nl' | 'en' | 'fr' | 'de';

interface Props {
  items: { value: string; label: string; lang: Language }[];
  defaultValue?: string;
  onChange?: (value: string) => void;
}

export function DropdownInput({ defaultValue, items, onChange }: Props) {
  const { t } = useTranslation(['translation', 'a11y']);
  const [isOpen, setIsOpen] = useState(false);
  const [currentValue, setCurrentValue] = useState(defaultValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const selectItem = (item: { value: string; label: string }) => {
    setCurrentValue(item.value);

    if (onChange) {
      onChange(item.value);
    }

    setTimeout(() => {
      setIsOpen(false);
      buttonRef.current?.focus();
    });
  };

  const isItemSelected = (item: { value: string; label: string }) =>
    currentValue === item.value;

  return (
    <div ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        className="dropdown-input__button"
        id="language-dropdown-button"
        data-testid="language-dropdown-button"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span
          aria-label={t('a11y:landing.header.languageDropdown.title', {
            selectedLanguage: t(
              `a11y:languages.${(currentValue ?? 'en') as Language}`
            ),
          })}
        >
          {items.find((i) => i.value === currentValue)?.label}
        </span>

        <span
          tabIndex={-1}
          className={classNames(
            'dropdown-input__button-icon',
            isOpen && 'dropdown-input__button-icon--open'
          )}
        >
          <BlokkieIcon
            aria-hidden="true"
            variant="chevronDown"
            color={blokkieTheme.colors.grey.white}
          />
        </span>
      </button>

      {isOpen && (
        <div className="dropdown-input__listbox-container">
          <ul
            role="listbox"
            className="dropdown-input__listbox"
            id="language-dropdown-list"
            data-testid="language-dropdown-list"
          >
            {items.map((item) => (
              <li
                key={item.value}
                role="option"
                tabIndex={0}
                aria-selected={currentValue === item.value}
                aria-label={t(`a11y:languages.${item.lang}`)}
                className={classNames(
                  'dropdown-input__option',
                  isItemSelected(item) && 'dropdown-input__option--selected'
                )}
                onClick={() => selectItem(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    selectItem(item);
                  }
                }}
                id="language-list-option"
                data-testid="language-list-option"
              >
                <BlokkieTypography variant="pl700" aria-hidden={true}>
                  {item.label}
                </BlokkieTypography>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
